import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, MenuItem,
  Select, Typography
} from "@mui/material";
import {
  Area, AreaChart, CartesianGrid,
  ComposedChart, Legend, ReferenceLine, ResponsiveContainer,
  Tooltip, XAxis, YAxis, Bar, LabelList
} from "recharts";
import {
  getDashboardCampaigns           as getCampaigns,
  getDashboardPaymentsSummary     as getPaymentsSummary,
  // Hourly/client-zone, not the old UTC-daily ptp-summary. payments-summary
  // (below) already bucketed live from customerconvevent in the tenant zone;
  // the funnel's Sent/Responded steps were the one thing on this page still
  // reading a UTC source, which mixed bases inside a single chart. Now both
  // halves of the funnel agree.
  getDashboardPtpSummaryHourly    as getPtpSummary,
  getDashboardAccountsHourly      as getAccounts,
} from "../../services/pbi/pbiservices";
import {
  useClientTimeZone, zonedNow, zoneAbbr,
} from "../../utils/clienttimezone";

// ── Constants ──────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const PINK   = "#D4537E";
const GREEN  = "#1D9E75";
const AMBER  = "#BA7517";
const PURPLE = "#7F77DD";
// Dotted average rule and the figure beside each chart title. A hue no series
// uses, so the annotation never reads as another measurement.
const AVG_INK  = "#0F6E56";

// ── ONE COLOUR PER METRIC ──────────────────────────────────────────
// A metric reads the same hue wherever it appears: its funnel bar, the figure
// on its KPI tile, its line, its area, its bar, its table column.
//
// Before this the same funnel had RPC and Kept both green and Sent and Kept
// both blue, and the same metric took a different hue on each page — Kept was
// green here, blue on Payments, deep blue on the 24-hr page. Colour was
// decoration; now it identifies.
//
// Chosen for separation rather than family. Kept is a component of PTP and
// Uncommitted is the rest of it, but shading them as siblings of one hue
// makes them hard to tell apart in a legend, which is the one place they are
// read side by side.
const C_SENT        = "#0C447C";  // navy
const C_ACCOUNTS    = "#378ADD";  // blue
const C_RPC         = "#1D9E75";  // green
const C_PTP         = "#BA7517";  // amber
const C_KEPT        = "#7F77DD";  // purple
const C_SL          = "#D4537E";  // pink
const C_UNCOMMITTED = "#3DBE92";  // mint
const C_24HR        = "#A01F50";  // deep pink


const GRAN_OPTIONS    = ["Day", "Week", "Month"];
const RANGE_OPTIONS = [
  "1D", "2D", "3D",
  "7D", "30D", "90D", "1Y",
  "C Wk", "CW+Last",
  "This Mo", "Cur+Last", "Prev 2Mo", "Prev 3Mo",
];

// Presets resolve against the CONFIGURED client time zone (Configuration →
// Time Settings), not the browser's. zonedNow() returns a Date whose local
// field accessors read as the tenant's wall clock, so all the getDate()/
// setDate() arithmetic below and toLocal() keep working unchanged.
function getDateRange(preset, timeZone) {
  const now = zonedNow(timeZone);
  const y   = now.getFullYear();
  const m   = now.getMonth();
  let start, end;

  const dow        = now.getDay();
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() - ((dow + 6) % 7));
  thisMonday.setHours(0, 0, 0, 0);

  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);

  switch (preset) {
    case "1D":
      start = new Date(now); start.setDate(now.getDate() - 1);   end = now; break;
    case "2D":
      start = new Date(now); start.setDate(now.getDate() - 2);   end = now; break;
    case "3D":
      start = new Date(now); start.setDate(now.getDate() - 3);   end = now; break;
    case "7D":
      start = new Date(now); start.setDate(now.getDate() - 7);   end = now; break;
    case "30D":
      start = new Date(now); start.setDate(now.getDate() - 30);  end = now; break;
    case "90D":
      start = new Date(now); start.setDate(now.getDate() - 90);  end = now; break;
    case "1Y":
      start = new Date(now); start.setDate(now.getDate() - 365); end = now; break;
    case "C Wk":
      start = thisMonday; end = now; break;
    case "CW+Last":
      start = lastMonday; end = now; break;
    case "This Mo":
      start = new Date(y, m, 1);     end = now; break;
    case "Cur+Last":
      start = new Date(y, m - 1, 1); end = now; break;
    case "Prev 2Mo":
      start = new Date(y, m - 2, 1); end = new Date(y, m, 0); break;
    case "Prev 3Mo":
    default:
      start = new Date(y, m - 3, 1); end = new Date(y, m, 0); break;
  }

  // Serialize from the date parts — toISOString() converts to UTC first
  // which can shift the date by 1 day in negative-offset zones.
  const toLocal = (d) => {
    const y  = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const dy = String(d.getDate()).padStart(2, "0");
    return `${y}-${mo}-${dy}`;
  };
  return {
    startDate: toLocal(start),
    endDate:   toLocal(end),
  };
}

// Previous period = same duration as current, ending the day before current
// starts. Operates on YYYY-MM-DD strings, so it inherits whatever zone
// getDateRange() resolved in — no zone handling needed here.
function getPrevDateRange(startDate, endDate) {
  const parse = (s) => {
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(s);
  };
  const start  = parse(startDate);
  const end    = parse(endDate);
  const durMs  = end.getTime() - start.getTime();
  const dayMs  = 86_400_000;
  const prevEnd   = new Date(start.getTime() - dayMs);
  const prevStart = new Date(prevEnd.getTime() - durMs);
  const toLocal = (d) => {
    const yy = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const dy = String(d.getDate()).padStart(2, "0");
    return `${yy}-${mo}-${dy}`;
  };
  return { startDate: toLocal(prevStart), endDate: toLocal(prevEnd) };
}

// ── Helpers ────────────────────────────────────────────────────────
function fmtCurrency(n) {
  const num = Number(n);
  if (!n || isNaN(num)) return "$0.00";
  // Switch to M only once the K form would need 4 digits — i.e. once
  // it rounds to 1000.0K or more.
  if (Math.abs(Number((num / 1000).toFixed(1))) >= 1000)
    return `$${(num / 1_000_000).toFixed(2)}M`;
  if (Math.abs(num) >= 1000) return `$${(num / 1000).toFixed(1)}K`;
  return `$${num.toFixed(2)}`;
}

// Compact currency for the funnel — 1 decimal on K (matches PtpDashboard).
function fmtCurrencyK1(n) {
  const num = Number(n);
  if (!num || isNaN(num)) return "$0";
  // Switch to M only once the K form would need 4 digits — i.e. once
  // it rounds to 1000.0K or more.
  if (Math.abs(Number((num / 1000).toFixed(1))) >= 1000)
    return `$${(num / 1_000_000).toFixed(2)}M`;
  if (Math.abs(num) >= 1000) return `$${(num / 1000).toFixed(1)}K`;
  return `$${num.toFixed(0)}`;
}

// Graph currency — 1 decimal everywhere ($1.2K / $45.5). Used for chart
// axis ticks, tooltips, and inline value labels so the graphs stay
// readable without the extra digit. KPI tiles keep full 2-decimal
// fmtCurrency for precise headline numbers.
function fmtCurrencyG(n) {
  const num = Number(n);
  if (!n || isNaN(num)) return "$0.0";
  // Switch to M only once the K form would need 4 digits — i.e. once
  // it rounds to 1000.0K or more.
  if (Math.abs(Number((num / 1000).toFixed(1))) >= 1000)
    return `$${(num / 1_000_000).toFixed(2)}M`;
  if (Math.abs(num) >= 1000) return `$${(num / 1000).toFixed(1)}K`;
  return `$${num.toFixed(1)}`;
}

// Percent, for the Kept % chart. Two decimals by default so it matches the
// other dashboards; the chart's inline labels pass 0 to stay readable at
// day granularity.
function fmtPct(v, dec = 2) {
  const n = Number(v);
  if (v == null || isNaN(n)) return "—";
  return `${n.toFixed(dec)}%`;
}

function fmtDate(val) {
  if (!val) return "";
  const s = String(val).slice(0, 10);
  const d = new Date(s.replace(/-/g, "/"));
  if (isNaN(d)) return new Date(val).toLocaleDateString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
  });
  return d.toLocaleDateString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
  });
}

// Axis date label: "Tu 06/16" for day/week buckets (two-char weekday + MM/DD,
// no year — it adds nothing on a dated axis). Month buckets show
// "Jun 25" since a weekday on a month-start would be misleading.
function fmtAxisDate(val, gran = "day") {
  if (!val) return "";
  const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
  if (isNaN(d)) return String(val);
  if (String(gran).toLowerCase() === "month")
    return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
  const wd = ["Su","Mo","Tu","We","Th","Fr","Sa"][d.getDay()];                  // Tu
  const md = d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" }); // 06/16
  return `${wd} ${md}`;
}

function safeNum(v) {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

// ── AVERAGE RULE + HEADER FIGURE ───────────────────────────────────
// The two rules the scorecard uses, for the same reasons.
//
// activeMean — mean over buckets WITH ACTIVITY only. A day the system was not
// running is not a zero; counting it drags the rule down and makes every real
// day look above trend.
//
// pooledPct — for a RATE, total numerator over total denominator. NOT the mean
// of the per-bucket percentages, which weights a 40-send day the same as a
// 4,000-send one; on a book whose volume moves the two differ by multiples.
function activeMean(rows, key) {
  const active = (rows || []).filter(r => safeNum(r[key]) > 0);
  return active.length
    ? active.reduce((a, r) => a + safeNum(r[key]), 0) / active.length
    : 0;
}

function pooledPct(rows, numKey, denKey) {
  const den = (rows || []).reduce((a, r) => a + safeNum(r[denKey]), 0);
  return den
    ? ((rows || []).reduce((a, r) => a + safeNum(r[numKey]), 0) * 100) / den
    : 0;
}

// Build a delta object from current/prev numeric values.
//   up:   true if current value increased vs prev  → drives ▲/▼ arrow
//   good: true if the change is favorable          → drives green/red color
//   text: "+5.2% vs $1.2K" — pct change + actual prev value formatted
function buildDelta(cur, prev, { goodWhenUp = true, fmtVal } = {}) {
  const c = Number(cur);
  const p = Number(prev);
  if (!isFinite(c) || !isFinite(p) || p === 0) return null;
  const pct      = ((c - p) / p) * 100;
  const up       = pct > 0;
  const prevText = fmtVal ? fmtVal(p) : p.toLocaleString();
  return {
    up,
    good: goodWhenUp ? up : !up,
    text: `${up ? "+" : ""}${pct.toFixed(1)}% vs ${prevText}`,
  };
}

// ── Aggregation helpers (shared by current & prev period) ──────────
//
// Stats:
//   total       — sum of event counts across all days
//   totalAmt    — sum of $ amounts across all days
//   avgAmt      — total $ ÷ total count (true per-event average)
//   maxAmt      — max of per-day max values (highest single event in range)
//   minAmt      — min of per-day min values (lowest single event in range)
//   avgPerDay   — total count ÷ number of distinct summary_dates with activity
//                 (so a 30-day range with events on 12 days reports as
//                 count/12 — represents typical activity ON an active day,
//                 not diluted by zero-event days)
//   medianDaily — median of per-day average amounts (typical day's avg)
//   modeDaily   — mode of per-day averages rounded to nearest dollar
//                 (rounding makes the mode meaningful — without it, every
//                 daily avg is a unique float and the mode is degenerate)
//
// median/mode are computed on per-day aggregates because the API
// doesn't expose individual event amounts. Labels in the UI clarify
// "(daily)" so the basis is obvious to the reader.
function median(sorted) {
  if (!sorted.length) return 0;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

function modeRounded(values) {
  if (!values.length) return 0;
  const counts = new Map();
  let bestVal = values[0];
  let bestCnt = 0;
  for (const v of values) {
    const k = Math.round(v);
    const c = (counts.get(k) || 0) + 1;
    counts.set(k, c);
    if (c > bestCnt) { bestCnt = c; bestVal = k; }
  }
  return bestVal;
}

function aggregateGroup(rows, prefix) {
  const total    = (rows || []).reduce((a, b) => a + safeNum(b[`${prefix}_count`]), 0);
  const totalAmt = (rows || []).reduce((a, b) => a + safeNum(b[`${prefix}_total`]), 0);
  const maxVals  = (rows || []).map(r => safeNum(r[`${prefix}_max`])).filter(v => v > 0);
  const minVals  = (rows || []).map(r => safeNum(r[`${prefix}_min`])).filter(v => v > 0);

  // Per-day averages, only for days that had events of this type.
  // Filtering by count > 0 prevents zero-event days from polluting the
  // distribution. Falls back to deriving avg from total/count if the
  // API didn't return a precomputed avg.
  const dailyAvgs = (rows || [])
    .filter(r => safeNum(r[`${prefix}_count`]) > 0)
    .map(r => {
      const a = safeNum(r[`${prefix}_avg`]);
      if (a > 0) return a;
      const t = safeNum(r[`${prefix}_total`]);
      const c = safeNum(r[`${prefix}_count`]);
      return c > 0 ? t / c : 0;
    })
    .filter(v => v > 0);

  const activeDays = dailyAvgs.length;
  const sortedAvgs = [...dailyAvgs].sort((a, b) => a - b);

  return {
    total, totalAmt,
    maxAmt: maxVals.length ? Math.max(...maxVals) : 0,
    minAmt: minVals.length ? Math.min(...minVals) : 0,
    avgAmt: total > 0 ? totalAmt / total : 0,
    avgPerDay:   activeDays > 0 ? total / activeDays : 0,
    medianDaily: median(sortedAvgs),
    modeDaily:   modeRounded(dailyAvgs),
  };
}

// PTP = Kept + Uncommitted. Rows are folded into a synthetic `ptp_` prefix
// so aggregateGroup gives the combined figure the same distribution stats
// (avg/day, median, mode) it gives the two parts, rather than adding two
// separately-derived averages together.
function combineToPtp(rows) {
  return (rows || []).map(r => {
    const cnt   = safeNum(r.payment_count) + safeNum(r.promise_count);
    const tot   = safeNum(r.payment_total) + safeNum(r.promise_total);
    const maxes = [safeNum(r.payment_max), safeNum(r.promise_max)].filter(v => v > 0);
    const mins  = [safeNum(r.payment_min), safeNum(r.promise_min)].filter(v => v > 0);
    return {
      summary_date: r.summary_date,
      ptp_count: cnt,
      ptp_total: tot,
      ptp_avg:   cnt > 0 ? tot / cnt : 0,
      ptp_max:   maxes.length ? Math.max(...maxes) : 0,
      ptp_min:   mins.length  ? Math.min(...mins)  : 0,
    };
  });
}

// ── Sub-components ─────────────────────────────────────────────────

// KPI tile — 3-row vertical stack (% or avg hero / $ middle / count
// footer) with delta below. Matches the pattern used on responses
// pages and PtpDashboard so the two pages read as siblings.
//
// On this page there's no rate concept (no "% of sent" for $ flows),
// so the hero slot carries the average amount instead. Total $ goes
// in the middle row; count goes in the small footer.
//
// Slot priorities for the hero when values are missing:
//   1. percent  (when an explicit % is passed in — used for Kept/Broken %)
//   2. count    (numeric fallback)
//   3. amount   (when only $ matters)
//   4. em-dash  (empty)
// The $ row is suppressed if the amount was already promoted to hero.
function KpiTile({ label, count, amount, percent, color = BLUE, delta }) {
  const hasPercent = percent !== null && percent !== undefined;
  const hasCount   = count   !== null && count   !== undefined;
  const hasAmount  = amount  !== null && amount  !== undefined;

  let heroValue;
  let heroIsAmount = false;
  if      (hasPercent) heroValue = percent;
  else if (hasCount)   heroValue = count;
  else if (hasAmount)  { heroValue = amount; heroIsAmount = true; }
  else                 heroValue = "—";

  const heroColor       = (hasPercent || hasCount || hasAmount) ? color : "text.disabled";
  const showAmountRow   = hasAmount && !heroIsAmount;
  const showFooterCount = hasCount && hasPercent;

  return (
    <Box sx={{
      background: "var(--mui-palette-background-default, #f7f7f7)",
      borderRadius: 2,
      p: "10px 12px",
      minWidth: 0,
      display: "flex", flexDirection: "column", gap: "6px",
    }}>
      <Typography sx={{
        fontSize: 10, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {label}
      </Typography>

      {/* Hero row */}
      <Box sx={{ minHeight: 26 }}>
        <Typography sx={{
          fontSize: 20, fontWeight: 500, color: heroColor, lineHeight: 1.2,
        }}>
          {heroValue}
        </Typography>
      </Box>

      {/* Amount row */}
      <Box sx={{ minHeight: 22 }}>
        {showAmountRow ? (
          <Typography sx={{
            fontSize: 15, fontWeight: 500, color: "text.primary", lineHeight: 1.2,
          }}>
            {amount}
          </Typography>
        ) : (
          <Typography sx={{
            fontSize: 15, color: "text.disabled", lineHeight: 1.2,
          }}>
            —
          </Typography>
        )}
      </Box>

      {/* Count footer — small, accent color */}
      <Box sx={{ minHeight: 18 }}>
        {showFooterCount ? (
          <Typography sx={{
            fontSize: 12, fontWeight: 500, color, lineHeight: 1.2,
          }}>
            {count}
          </Typography>
        ) : (
          <Typography sx={{
            fontSize: 12, color: "text.disabled", lineHeight: 1.2,
          }}>
            —
          </Typography>
        )}
      </Box>

      {/* Delta — ▲/▼ vs prev period */}
      {delta && (
        <Typography sx={{ fontSize: 11, color: delta.good ? "#3B6D11" : "#A32D2D", mt: 0.25 }}>
          {delta.up ? "▲" : "▼"} {delta.text}
        </Typography>
      )}
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

// MiniStat — small label/value card used in the 4-stat strip
// (Total / Avg / Max / Min) under each amount-over-time section.
// Lighter than KpiTile: no hero hierarchy, no delta, no count footer.
// Just a quick distribution reference. Deltas already live on the
// grouped Activity tiles up top, so adding them here would be noise.
function MiniStat({ label, value, color = BLUE }) {
  return (
    <Box sx={{
      background: "var(--mui-palette-background-default, #f7f7f7)",
      borderRadius: 2, p: "8px 12px", minWidth: 0,
    }}>
      <Typography sx={{
        fontSize: 10, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.25,
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: 16, fontWeight: 500, color, lineHeight: 1.2,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {value}
      </Typography>
    </Box>
  );
}

// KpiCard — the original large card with delta arrow. Used in the
// per-metric breakout row (Total / Avg / Max / Min, each with its own
// delta vs prev period). Pairs visually with the lighter MiniStat
// strip and the headline grouped tile in the Activity section.
function KpiCard({ label, value, color = BLUE, delta }) {
  return (
    <Box sx={{
      background: "var(--mui-palette-background-default, #f7f7f7)",
      borderRadius: 2, p: "10px 14px", minWidth: 0,
    }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {delta && (
        <Typography sx={{ fontSize: 11, color: delta.good ? "#3B6D11" : "#A32D2D", mt: 0.5 }}>
          {delta.up ? "▲" : "▼"} {delta.text}
        </Typography>
      )}
    </Box>
  );
}

function ChartCard({ title, right, children, height = 200 }) {
  return (
    <Box sx={{
      border: "0.5px solid", borderColor: "divider",
      borderRadius: 2, p: "12px 14px",
    }}>
      {(title || right) && (
        <Box sx={{ display: "flex", alignItems: "baseline",
                   justifyContent: "space-between", gap: 1, mb: 1 }}>
          <Typography sx={{
            fontSize: 11, fontWeight: 500, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.05em",
          }}>
            {title}
          </Typography>
          {right && (
            <Typography sx={{ fontSize: 11, color: AVG_INK, whiteSpace: "nowrap" }}>
              {right}
            </Typography>
          )}
        </Box>
      )}
      <Box sx={{ width: "100%", height: height === "auto" ? "auto" : height }}>
        {children}
      </Box>
    </Box>
  );
}

function PillGroup({ label, options, value, onChange, groups }) {
  let groupBoundaries = new Set();
  if (groups) {
    let idx = 0;
    groups.forEach((size, gi) => {
      if (gi < groups.length - 1) { idx += size; groupBoundaries.add(idx); }
    });
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{
        fontSize: 11, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em",
      }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {options.map((opt, i) => {
          const active = value === opt;
          return (
            <React.Fragment key={opt}>
              {groupBoundaries.has(i) && (
                <Box sx={{ width: "1px", height: 18, bgcolor: "divider", mx: 0.5, flexShrink: 0 }} />
              )}
              <Box
                onClick={() => onChange(opt)}
                sx={{
                  px: "10px", py: "3px", fontSize: 12, borderRadius: "20px",
                  cursor: "pointer", userSelect: "none",
                  border: "0.5px solid",
                  borderColor: active ? BLUE : "divider",
                  background:  active ? BLUE : "transparent",
                  color:       active ? "#fff" : "text.secondary",
                  fontWeight:  active ? 500 : 400,
                  transition:  "all .15s",
                  "&:hover": { borderColor: BLUE },
                }}
              >
                {opt}
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Semi-circle gauge ──────────────────────────────────────────────
function GaugeCard({ label, value, subLabel, subValue, subColor = AMBER, pct = 0, gaugeColor = BLUE }) {
  const clampedPct = Math.min(100, Math.max(0, safeNum(pct)));
  const r    = 52;
  const cx   = 70;
  const cy   = 66;
  const circ = Math.PI * r;
  const dash = (clampedPct / 100) * circ;

  return (
    <Box sx={{
      border: "0.5px solid", borderColor: "divider",
      borderRadius: 2, p: "12px 14px",
      display: "flex", flexDirection: "column", alignItems: "center",
    }}>
      <Typography sx={{
        fontSize: 11, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em",
        mb: 1, alignSelf: "flex-start",
      }}>
        {label}
      </Typography>
      <svg width={140} height={76} style={{ overflow: "visible" }}>
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none" stroke="#e8e8e8" strokeWidth={12} strokeLinecap="round"
        />
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none" stroke={gaugeColor} strokeWidth={12} strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`}
        />
        <text x={cx} y={cy - 6} textAnchor="middle"
          style={{ fontSize: 16, fontWeight: 500, fill: gaugeColor }}>
          {value}
        </text>
      </svg>
      {subLabel && (
        <>
          <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
            {subLabel}
          </Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 500, color: subColor, mt: 0.5 }}>
            {subValue}
          </Typography>
        </>
      )}
    </Box>
  );
}

// ── Funnel ─────────────────────────────────────────────────────────
// Left-aligned colored fill inside a full-width GRAY track. `steps` is
// [{ label, val, color, den?, amount? }]; the first step is the baseline
// and fills completely. Every other step's fill length equals the
// percentage printed inside it, so the bar and the number agree.
//
// Each step past the first carries its own denominator (`den`) for the
// in-bar percentage, so a step can be read against whatever base makes
// sense for it: RPC against Accounts, PTP and Smart Links against RPC,
// Kept against PTP. The share of Sent drops to the small line under the
// label. Steps with no `den` (Accounts itself) keep % of Sent in the bar.
function Funnel({ steps }) {
  const base = (steps && steps[0] && steps[0].val) || 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, py: 0.5 }}>
      {(steps || []).map((s, i) => {
        const pctOfSent = base > 0 ? (s.val / base) * 100 : 0;
        const denPct    = s.den > 0 ? (s.val / s.den) * 100 : null;
        const isFirst   = i === 0;
        // The fill is the number printed inside the bar, 1:1 — no
        // magnification. A step with its own `den` fills to its share of
        // that base; the rest fill to their share of Sent. Floored at 2%
        // so a near-zero step still shows a sliver of color.
        const shownPct  = isFirst ? 100 : (denPct != null ? denPct : pctOfSent);
        const fillW     = shownPct > 0 ? Math.min(100, Math.max(shownPct, 2)) : 0;
        return (
          <Box key={s.label} sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
            {/* Left: fixed-width track holding the centered, resizing gray bar */}
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", justifyContent: "center" }}>
              <Box sx={{
                position: "relative", width: "100%", minWidth: 0,
                height: 46, background: "#f0f0f0", borderRadius: "4px",
                transition: "width .4s ease",
              }}>
                <Box sx={{
                  position: "absolute", left: 0, top: 0, bottom: 0,
                  width: `${fillW}%`, background: s.color,
                  borderRadius: isFirst ? "4px" : "4px 0 0 4px",
                }} />
                {(
                  <Box sx={{
                    position: "absolute", inset: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 600, color: isFirst ? "#fff" : "#333",
                    pointerEvents: "none", whiteSpace: "nowrap",
                  }}>
                    {isFirst
                      ? Number(s.val).toLocaleString()
                      : denPct != null
                        ? `${denPct.toFixed(1)}%`
                        : `${pctOfSent.toFixed(1)}%`}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Right: label on line 1, then count + $ on line 2, % of Sent below */}
            <Box sx={{
              width: 90, flexShrink: 0, textAlign: "left",
              fontSize: 12, color: "text.secondary", lineHeight: 1.35,
            }}>
              <Box sx={{ color: s.color, fontWeight: 600 }}>{s.label}</Box>
              <Box sx={{ mt: 0.25, whiteSpace: "nowrap" }}>
                {/* Count and $ on line 2; % of Sent drops to its own smaller line below. */}
                {!isFirst && <span style={{ fontWeight: 500 }}>{Number(s.val).toLocaleString()}</span>}
                {!isFirst && s.amount != null && (
                  <span> · <b style={{ color: s.color }}>{fmtCurrencyK1(s.amount)}</b></span>
                )}
              </Box>
              {denPct != null && (
                <Box sx={{ mt: 0.25, fontSize: 10, color: "text.disabled", whiteSpace: "nowrap" }}>
                  {pctOfSent.toFixed(1)}% of Sent
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────
export default function PaymentsDashboard({ channel }) {
  // Day / 30D on open: a daily series over a month is the view people
  // land on this page for. Week/90D smoothed away the movement the
  // page exists to show, and had every visit start with two clicks.
  const [gran,        setGran]        = useState("Day");
  const [rangePreset, setRangePreset] = useState("30D");
  const [campaign,    setCampaign]    = useState("All");
  const [campaigns,   setCampaigns]   = useState(["All"]);

  const [summary,         setSummary]         = useState([]);
  // Prev-period dataset — used for KPI deltas only, not charted.
  const [prevSummary,     setPrevSummary]     = useState([]);
  // PTP summary — only for the funnel's Sent + RPC steps.
  const [ptpSummary,      setPtpSummary]      = useState({});
  // Accounts — its own endpoint; current period only, no delta.
  const [accounts,        setAccounts]        = useState(0);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState("");

  // Configured client zone drives every date on this page.
  const { timeZone, tzReady } = useClientTimeZone();

  const channelParam = (channel || "texts").toLowerCase();

  useEffect(() => {
    getCampaigns()
      .then(data => setCampaigns(["All", ...(data || []).map(c => c.name)]))
      .catch(() => {});
  }, []);

  const params = useMemo(() => ({
    ...getDateRange(rangePreset, timeZone),
    campaign:    campaign === "All" ? undefined : campaign,
    granularity: gran.toLowerCase(),
    channel:     channelParam,
  }), [rangePreset, campaign, gran, channelParam, timeZone]);

  const prevParams = useMemo(() => ({
    ...getPrevDateRange(params.startDate, params.endDate),
    campaign:    campaign === "All" ? undefined : campaign,
    granularity: gran.toLowerCase(),
    channel:     channelParam,
  }), [params.startDate, params.endDate, campaign, gran, channelParam]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [s, ps, pp, acc] = await Promise.all([
        getPaymentsSummary(params),
        getPaymentsSummary(prevParams).catch(() => []),
        getPtpSummary(params).catch(() => ({})),
        getAccounts(params).catch(() => ({})),
      ]);
      setSummary(s      || []);
      setPrevSummary(ps || []);
      setPtpSummary(pp  || {});
      setAccounts(safeNum(acc?.account_count));
    } catch (e) {
      console.error("Dashboard load error:", e?.response?.status, e?.response?.data, e?.message);
      setError(`Failed to load payments data: ${e?.response?.data?.error || e?.message || "unknown"}`);
    } finally {
      setLoading(false);
    }
  }, [params, prevParams]);

  // Gated on tzReady — firing before the client config lands would request a
  // UTC-derived window and then immediately refetch with the real zone.
  useEffect(() => { if (tzReady) loadAll(); }, [loadAll, tzReady]);

  // ── KPI aggregates per metric group ────────────────────────────
  // Each tile pulls from one aggregate so values are clearly tied to
  // their source. The grouped Activity tile uses avgDelta (the hero);
  // the 4 large per-metric KpiCards each use their own delta so the
  // arrow on each card matches its headline value.
  const payKpis = useMemo(() => {
    const cur  = aggregateGroup(summary,     "payment");
    const prev = aggregateGroup(prevSummary, "payment");
    const cashOpts  = { goodWhenUp: true, fmtVal: v => fmtCurrency(v) };
    const countOpts = { goodWhenUp: true, fmtVal: v => v.toFixed(1) };
    return {
      ...cur,
      totalDelta:       buildDelta(cur.total,       prev.total,       { goodWhenUp: true }),
      totalAmtDelta:    buildDelta(cur.totalAmt,    prev.totalAmt,    cashOpts),
      avgDelta:         buildDelta(cur.avgAmt,      prev.avgAmt,      cashOpts),
      maxAmtDelta:      buildDelta(cur.maxAmt,      prev.maxAmt,      cashOpts),
      minAmtDelta:      buildDelta(cur.minAmt,      prev.minAmt,      cashOpts),
      avgPerDayDelta:   buildDelta(cur.avgPerDay,   prev.avgPerDay,   countOpts),
      medianDailyDelta: buildDelta(cur.medianDaily, prev.medianDaily, cashOpts),
      modeDailyDelta:   buildDelta(cur.modeDaily,   prev.modeDaily,   cashOpts),
    };
  }, [summary, prevSummary]);

  const proKpis = useMemo(() => {
    const cur  = aggregateGroup(summary,     "promise");
    const prev = aggregateGroup(prevSummary, "promise");
    const cashOpts  = { goodWhenUp: true, fmtVal: v => fmtCurrency(v) };
    const countOpts = { goodWhenUp: true, fmtVal: v => v.toFixed(1) };
    return {
      ...cur,
      totalDelta:       buildDelta(cur.total,       prev.total,       { goodWhenUp: true }),
      totalAmtDelta:    buildDelta(cur.totalAmt,    prev.totalAmt,    cashOpts),
      avgDelta:         buildDelta(cur.avgAmt,      prev.avgAmt,      cashOpts),
      maxAmtDelta:      buildDelta(cur.maxAmt,      prev.maxAmt,      cashOpts),
      minAmtDelta:      buildDelta(cur.minAmt,      prev.minAmt,      cashOpts),
      avgPerDayDelta:   buildDelta(cur.avgPerDay,   prev.avgPerDay,   countOpts),
      medianDailyDelta: buildDelta(cur.medianDaily, prev.medianDaily, cashOpts),
      modeDailyDelta:   buildDelta(cur.modeDaily,   prev.modeDaily,   cashOpts),
    };
  }, [summary, prevSummary]);

  const slKpis = useMemo(() => {
    const cur  = aggregateGroup(summary,     "smartlink");
    const prev = aggregateGroup(prevSummary, "smartlink");
    const cashOpts  = { goodWhenUp: true, fmtVal: v => fmtCurrency(v) };
    const countOpts = { goodWhenUp: true, fmtVal: v => v.toFixed(1) };
    return {
      ...cur,
      totalDelta:       buildDelta(cur.total,       prev.total,       { goodWhenUp: true }),
      totalAmtDelta:    buildDelta(cur.totalAmt,    prev.totalAmt,    cashOpts),
      avgDelta:         buildDelta(cur.avgAmt,      prev.avgAmt,      cashOpts),
      maxAmtDelta:      buildDelta(cur.maxAmt,      prev.maxAmt,      cashOpts),
      minAmtDelta:      buildDelta(cur.minAmt,      prev.minAmt,      cashOpts),
      avgPerDayDelta:   buildDelta(cur.avgPerDay,   prev.avgPerDay,   countOpts),
      medianDailyDelta: buildDelta(cur.medianDaily, prev.medianDaily, cashOpts),
      modeDailyDelta:   buildDelta(cur.modeDaily,   prev.modeDaily,   cashOpts),
    };
  }, [summary, prevSummary]);

  // PTP — Kept + Uncommitted, the denominator Kept is read against.
  const ptpKpis = useMemo(() => {
    const cur  = aggregateGroup(combineToPtp(summary),     "ptp");
    const prev = aggregateGroup(combineToPtp(prevSummary), "ptp");
    const cashOpts  = { goodWhenUp: true, fmtVal: v => fmtCurrency(v) };
    const countOpts = { goodWhenUp: true, fmtVal: v => v.toFixed(1) };
    return {
      ...cur,
      totalDelta:       buildDelta(cur.total,       prev.total,       { goodWhenUp: true }),
      totalAmtDelta:    buildDelta(cur.totalAmt,    prev.totalAmt,    cashOpts),
      avgDelta:         buildDelta(cur.avgAmt,      prev.avgAmt,      cashOpts),
      maxAmtDelta:      buildDelta(cur.maxAmt,      prev.maxAmt,      cashOpts),
      minAmtDelta:      buildDelta(cur.minAmt,      prev.minAmt,      cashOpts),
      avgPerDayDelta:   buildDelta(cur.avgPerDay,   prev.avgPerDay,   countOpts),
      medianDailyDelta: buildDelta(cur.medianDaily, prev.medianDaily, cashOpts),
      modeDailyDelta:   buildDelta(cur.modeDaily,   prev.modeDaily,   cashOpts),
    };
  }, [summary, prevSummary]);

  // ── Chart data ─────────────────────────────────────────────────
  const payChartData = useMemo(() =>
    [...summary]
      .sort((a, b) => String(a.summary_date).localeCompare(String(b.summary_date)))
      .map(r => ({
        date:       fmtAxisDate(r.summary_date, gran),
        payments:   safeNum(r.payment_count),
        payAmt:     safeNum(r.payment_total),
        promises:   safeNum(r.promise_count),
        proAmt:     safeNum(r.promise_total),
        ptpAmt:     safeNum(r.payment_total) + safeNum(r.promise_total),
        // Kept as a share of PTP, per bucket — the rate the funnel's Kept
        // step shows for the window, drawn over time.
        ptpCount:   safeNum(r.payment_count) + safeNum(r.promise_count),
        keptPct:    (safeNum(r.payment_count) + safeNum(r.promise_count))
                      ? (safeNum(r.payment_count) * 100)
                        / (safeNum(r.payment_count) + safeNum(r.promise_count))
                      : 0,
        smartlinks: safeNum(r.smartlink_count),
        slAmt:      safeNum(r.smartlink_total),
      }))
  , [summary, gran]);

  // Conversion funnel: Sent → Accounts → RPC → PTP → Kept → Smart Links.
  // Sent + RPC come from the PTP summary; the rest reuse the page totals so
  // the funnel matches the numbers shown elsewhere on the page. Labels are
  // display-only renames: "RPC" is rpc_count, "Kept" is the payments group,
  // "PTP" is Kept + Uncommitted.
  // RPC is read against Accounts; PTP and Smart Links against RPC; Kept
  // against PTP.
  const funnelSteps = useMemo(() => {
    // RPC gross of opt-outs — rpc_count from the route is net.
    const rpc = safeNum(ptpSummary.rpc_count) + safeNum(ptpSummary.optout_count);
    return [
      { label: "Sent",        val: safeNum(ptpSummary.texts_sent), color: C_SENT },
      { label: "Accounts",    val: accounts,                       color: C_ACCOUNTS },
      { label: "RPC",         val: rpc,                            color: C_RPC, den: accounts },
      { label: "PTP",         val: ptpKpis.total, amount: ptpKpis.totalAmt, color: C_PTP,  den: rpc },
      { label: "Kept",        val: payKpis.total, amount: payKpis.totalAmt, color: C_KEPT, den: ptpKpis.total },
      { label: "Smart Links", val: slKpis.total,  amount: slKpis.totalAmt,  color: C_SL,   den: rpc },
    ];
  }, [ptpSummary, accounts, payKpis.total, ptpKpis.total, slKpis.total, payKpis.totalAmt, ptpKpis.totalAmt, slKpis.totalAmt]);

  const axisStyle    = { fontSize: 10, fill: "#888" };
  const tooltipStyle = { fontSize: 12 };
  const isEmails = channelParam === "emails";
  const tzLabel  = zoneAbbr(timeZone);

  // Mean of the buckets that had activity, per amount series.
  const avgKeptPct = pooledPct(payChartData, "payments", "ptpCount");
  const avgKeptAmt = activeMean(payChartData, "payAmt");
  const avgPtpAmt  = activeMean(payChartData, "ptpAmt");
  const avgUncAmt  = activeMean(payChartData, "proAmt");
  const avgSlAmt   = activeMean(payChartData, "slAmt");
  // ── THE RANGE THE PILLS RESOLVED TO ──
  // Appended to every chart title and the funnel, the way the scorecard does
  // it. The pills say "90D"; only this says which 90 days, and a screenshot
  // or a PDF export carries no pill state at all.
  const rangeLabel = `${fmtDate(params.startDate)} – ${fmtDate(params.endDate)}`;
  const titleOf    = (t) => (rangeLabel ? `${t} · ${rangeLabel}` : t);



  return (
    <Box sx={{ p: 2 }}>

      {/* ── Slicers ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2,
        alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <PillGroup
          label="Granularity"
          options={GRAN_OPTIONS}
          value={gran}
          onChange={setGran}
        />
        <PillGroup
          label="Date range"
          options={RANGE_OPTIONS}
          value={rangePreset}
          onChange={setRangePreset}
          groups={[3, 4, 2, 4]}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignSelf: "flex-end" }}>
          <Typography sx={{
            fontSize: 11, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.05em",
          }}>
            Campaign
          </Typography>
          <Select
            value={campaign}
            onChange={e => setCampaign(e.target.value)}
            size="small" variant="outlined"
            sx={{
              fontSize: 12, borderRadius: "20px", minWidth: 120, height: 28,
              ".MuiOutlinedInput-notchedOutline": { borderColor: "divider", borderWidth: "0.5px" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
              ".MuiSelect-select": { py: "3px", px: "10px", fontSize: 12, color: "text.secondary" },
              ".MuiSvgIcon-root": { fontSize: 16, color: "text.secondary" },
            }}
          >
            {campaigns.map(c => (
              <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>
            ))}
          </Select>
        </Box>

        {/* Zone the dates on this page resolve against */}
        <Box sx={{ ml: "auto", alignSelf: "flex-end", fontSize: 11, color: "text.secondary" }}>
          Times shown in {tzLabel}
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
          <CircularProgress size={20} /> Loading payments data…
        </Box>
      )}

      {/* ── Emails: not available notice ── */}
      {!loading && isEmails && (
        <Box sx={{
          mt: 2, p: 3, borderRadius: 2,
          border: "0.5px solid", borderColor: "divider",
          color: "text.secondary", fontSize: 13,
          display: "flex", alignItems: "center", gap: 1,
        }}>
          Payment and promise data is only available for the Texts channel.
        </Box>
      )}

      {/* ── Texts: full dashboard ── */}
      {!loading && !isEmails && (
        <>
          {/* ── KEPT AMOUNT OVER TIME ─────────────────────────
              Kept is the payments group (CF-AI3-PAY) — the page's headline
              flow, so it leads and carries the funnel beside it. PTP, the
              base Kept is measured against, sits directly below. */}
          <SectionTitle>Kept</SectionTitle>

          {/* The page's KPI row: Total / Avg / Max / Min. Counts and rates
              live in the funnel, so they are not repeated here — this row is
              only about the shape of the amounts. */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))",
            gap: 1, mb: 1 }}>
            <MiniStat label="Total" value={fmtCurrency(payKpis.totalAmt)} color={C_KEPT} />
            <MiniStat label="Avg"   value={fmtCurrency(payKpis.avgAmt)}   color={C_KEPT} />
            <MiniStat label="Max"   value={fmtCurrency(payKpis.maxAmt)}   color={C_KEPT} />
            <MiniStat label="Min"   value={fmtCurrency(payKpis.minAmt)}   color={C_KEPT} />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "3fr 1fr", gap: 1.5, mb: 0.5 }}>
            <ChartCard title={titleOf("Kept % of PTP")} right={`avg ${fmtPct(avgKeptPct)}`} height={300}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={payChartData} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="keptPctGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C_KEPT} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={C_KEPT} stopOpacity={0}    />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={axisStyle} />
                  <YAxis tick={axisStyle} tickFormatter={v => `${Math.round(v)}%`} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [fmtPct(v), n]} />
                  <ReferenceLine y={avgKeptPct} stroke={AVG_INK} strokeDasharray="3 3" strokeWidth={1} />
                  <Area type="monotone" dataKey="keptPct" name="Kept % of PTP"
                    stroke={C_KEPT} fill="url(#keptPctGrad)" strokeWidth={2} dot={{ r: 3 }}>
                    <LabelList dataKey="keptPct" position="top" offset={8}
                      fontSize={10} fill={C_KEPT} formatter={v => fmtPct(v, 0)} />
                  </Area>
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Conversion funnel" height="auto">
              <Funnel steps={funnelSteps} />
            </ChartCard>
          </Box>

          {/* ── Methodology note ── */}
          <Box sx={{
            mb: 1.5, p: "10px 14px", borderRadius: 2,
            border: "0.5px solid", borderColor: `${AMBER}55`,
            background: `${AMBER}0F`, fontSize: 12, color: "text.secondary", lineHeight: 1.5,
          }}>
            <b style={{ color: AMBER }}>Note:</b>{" "}
            <b>RPC</b> is the count of customers who responded to the text message,
            including opt-outs. <b>Kept</b> are immediate payments processed through PNM,{" "}
            <b>Uncommitted</b> are future payments scheduled through PNM, and <b>PTP</b> is
            the two together — the base Kept is measured against. Every figure on this page,
            including the funnel's <b>Sent</b> and <b>RPC</b> steps, is bucketed in{" "}
            <b>{tzLabel}</b> — the day is taken after converting each event's timestamp, so
            conversion rates hold exactly across the whole funnel, at any range length.
          </Box>

          {/* Kept $ — the first of the four amount charts. */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 240px", gap: 1.5, mb: 1.5 }}>
            <ChartCard title={titleOf("Kept $")} right={`avg ${fmtCurrencyG(avgKeptAmt)}`} height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={payChartData} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="payGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C_KEPT} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={C_KEPT} stopOpacity={0}    />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={axisStyle} />
                  <YAxis tick={axisStyle} tickFormatter={v => fmtCurrencyG(v)} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [fmtCurrencyG(v), n]} />
                  <ReferenceLine y={avgKeptAmt} stroke={AVG_INK} strokeDasharray="3 3" strokeWidth={1} />
                  <Area type="monotone" dataKey="payAmt" name="Kept amount"
                    stroke={C_KEPT} fill="url(#payGrad)" strokeWidth={2} dot={{ r: 3 }}>
                    <LabelList dataKey="payAmt" position="top" offset={8}
                      fontSize={10} fill={C_KEPT} formatter={v => fmtCurrencyG(v)} />
                  </Area>
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <GaugeCard
              label="Avg Kept amount"
              value={fmtCurrency(payKpis.avgAmt)}
              subLabel="Total Kept amount"
              subValue={fmtCurrency(payKpis.totalAmt)}
              subColor={C_PTP}
              gaugeColor={C_KEPT}
              pct={payKpis.totalAmt > 0 ? Math.min(100, (payKpis.avgAmt / payKpis.totalAmt) * 1000) : 0}
            />
          </Box>

          {/* ── PTP AMOUNT OVER TIME ──────────────────────────
              PTP is Kept + Uncommitted — the base Kept is measured against,
              so it sits directly under Kept rather than leading. */}
          <SectionTitle>PTP</SectionTitle>

          {/* Max/Min are the wider of the two parts, not their sum. */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))",
            gap: 1, mb: 1 }}>
            <MiniStat label="Total" value={fmtCurrency(ptpKpis.totalAmt)} color={C_PTP} />
            <MiniStat label="Avg"   value={fmtCurrency(ptpKpis.avgAmt)}   color={C_PTP} />
            <MiniStat label="Max"   value={fmtCurrency(ptpKpis.maxAmt)}   color={C_PTP} />
            <MiniStat label="Min"   value={fmtCurrency(ptpKpis.minAmt)}   color={C_PTP} />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 240px", gap: 1.5, mb: 1.5 }}>
            <ChartCard title={titleOf("PTP $")} right={`avg ${fmtCurrencyG(avgPtpAmt)}`} height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={payChartData} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="ptpGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C_PTP} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={C_PTP} stopOpacity={0}    />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={axisStyle} />
                  <YAxis tick={axisStyle} tickFormatter={v => fmtCurrencyG(v)} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [fmtCurrencyG(v), n]} />
                  <ReferenceLine y={avgPtpAmt} stroke={AVG_INK} strokeDasharray="3 3" strokeWidth={1} />
                  <Area type="monotone" dataKey="ptpAmt" name="PTP amount"
                    stroke={C_PTP} fill="url(#ptpGrad)" strokeWidth={2} dot={{ r: 3 }}>
                    <LabelList dataKey="ptpAmt" position="top" offset={8}
                      fontSize={10} fill={C_PTP} formatter={v => fmtCurrencyG(v)} />
                  </Area>
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <GaugeCard
              label="Avg PTP amount"
              value={fmtCurrency(ptpKpis.avgAmt)}
              subLabel="Total PTP amount"
              subValue={fmtCurrency(ptpKpis.totalAmt)}
              subColor={BLUE}
              gaugeColor={C_PTP}
              pct={ptpKpis.totalAmt > 0 ? Math.min(100, (ptpKpis.avgAmt / ptpKpis.totalAmt) * 1000) : 0}
            />
          </Box>

          {/* ── UNCOMMITTED AMOUNT OVER TIME ───────────────────
              The promise group (CF-AI4-PTP) — PTP minus Kept. */}
          <SectionTitle>Uncommitted</SectionTitle>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))",
            gap: 1, mb: 1 }}>
            <MiniStat label="Total" value={fmtCurrency(proKpis.totalAmt)} color={C_UNCOMMITTED} />
            <MiniStat label="Avg"   value={fmtCurrency(proKpis.avgAmt)}   color={C_UNCOMMITTED} />
            <MiniStat label="Max"   value={fmtCurrency(proKpis.maxAmt)}   color={C_UNCOMMITTED} />
            <MiniStat label="Min"   value={fmtCurrency(proKpis.minAmt)}   color={C_UNCOMMITTED} />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 240px", gap: 1.5, mb: 1.5 }}>
            <ChartCard title={titleOf("Uncommitted $")} right={`avg ${fmtCurrencyG(avgUncAmt)}`} height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={payChartData} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="proGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C_UNCOMMITTED} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={C_UNCOMMITTED} stopOpacity={0}    />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={axisStyle} />
                  <YAxis tick={axisStyle} tickFormatter={v => fmtCurrencyG(v)} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [fmtCurrencyG(v), n]} />
                  <ReferenceLine y={avgUncAmt} stroke={AVG_INK} strokeDasharray="3 3" strokeWidth={1} />
                  <Area type="monotone" dataKey="proAmt" name="Uncommitted amount"
                    stroke={C_UNCOMMITTED} fill="url(#proGrad)" strokeWidth={2} dot={{ r: 3 }}>
                    <LabelList dataKey="proAmt" position="top" offset={8}
                      fontSize={10} fill={C_UNCOMMITTED} formatter={v => fmtCurrencyG(v)} />
                  </Area>
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <GaugeCard
              label="Avg Uncommitted amount"
              value={fmtCurrency(proKpis.avgAmt)}
              subLabel="Total Uncommitted amount"
              subValue={fmtCurrency(proKpis.totalAmt)}
              subColor={AMBER}
              gaugeColor={C_UNCOMMITTED}
              pct={proKpis.totalAmt > 0 ? Math.min(100, (proKpis.avgAmt / proKpis.totalAmt) * 1000) : 0}
            />
          </Box>

          {/* ── SMART LINK AMOUNT OVER TIME ───────────────────── */}
          <SectionTitle>Smart Links</SectionTitle>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))",
            gap: 1, mb: 1 }}>
            <MiniStat label="Total" value={fmtCurrency(slKpis.totalAmt)} color={C_SL} />
            <MiniStat label="Avg"   value={fmtCurrency(slKpis.avgAmt)}   color={C_SL} />
            <MiniStat label="Max"   value={fmtCurrency(slKpis.maxAmt)}   color={C_SL} />
            <MiniStat label="Min"   value={fmtCurrency(slKpis.minAmt)}   color={C_SL} />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 240px", gap: 1.5, mb: 1.5 }}>
            <ChartCard title={titleOf("Smart Links $")} right={`avg ${fmtCurrencyG(avgSlAmt)}`} height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={payChartData} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="slGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C_SL} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={C_SL} stopOpacity={0}    />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={axisStyle} />
                  <YAxis tick={axisStyle} tickFormatter={v => fmtCurrencyG(v)} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [fmtCurrencyG(v), n]} />
                  <ReferenceLine y={avgSlAmt} stroke={AVG_INK} strokeDasharray="3 3" strokeWidth={1} />
                  <Area type="monotone" dataKey="slAmt" name="Offer amount"
                    stroke={C_SL} fill="url(#slGrad)" strokeWidth={2} dot={{ r: 3 }}>
                    <LabelList dataKey="slAmt" position="top" offset={8}
                      fontSize={10} fill={C_SL} formatter={v => fmtCurrencyG(v)} />
                  </Area>
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <GaugeCard
              label="Avg offer amount"
              value={fmtCurrency(slKpis.avgAmt)}
              subLabel="Total offer amount"
              subValue={fmtCurrency(slKpis.totalAmt)}
              subColor={PURPLE}
              gaugeColor={C_SL}
              pct={slKpis.totalAmt > 0 ? Math.min(100, (slKpis.avgAmt / slKpis.totalAmt) * 1000) : 0}
            />
          </Box>

          {/* ── OVERVIEW ──────────────────────────────────────── */}
          <SectionTitle>Overview</SectionTitle>

          <ChartCard title={titleOf("Kept vs Uncommitted vs Smart Links")} height={220}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={payChartData} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis yAxisId="left"  tick={axisStyle} />
                <YAxis yAxisId="right" orientation="right" tick={axisStyle} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="left" dataKey="payments"   name="Kept"        fill={C_KEPT}   radius={[3,3,0,0]}>
                  <LabelList dataKey="payments" position="top" fontSize={10} fill={C_KEPT} />
                </Bar>
                <Bar yAxisId="left" dataKey="promises"   name="Uncommitted" fill={C_UNCOMMITTED}  radius={[3,3,0,0]}>
                  <LabelList dataKey="promises" position="top" fontSize={10} fill={C_UNCOMMITTED} />
                </Bar>
                <Bar yAxisId="left" dataKey="smartlinks" name="Smart Links" fill={C_SL} radius={[3,3,0,0]}>
                  <LabelList dataKey="smartlinks" position="top" fontSize={10} fill={C_SL} />
                </Bar>
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

        </>
      )}
    </Box>
  );
}